import {
  ComponentManifest_base_default,
  EditingBehaviors_base_default,
  Style_base_default
} from "../chunk-NDOS2GQF.js";
import "../chunk-WGEOEJLG.js";
import "../chunk-CBT2ZTXA.js";

// src/resolver/componentManifestResolver.constants.ts
var constants = {
  notFetcherError: "ComponentManifestResolver: fetchManifest function is not defined",
  failedMergingError: ({
    mainManifest,
    referredManifest,
    errors
  }) => `Error: Failed merging between ${JSON.stringify(mainManifest)} to ${JSON.stringify(referredManifest)}
Errors : ${JSON.stringify(errors)}`
};
var componentManifestResolver_constants_default = constants;

// src/resolver/resolvers/EditingBehaviors/EditingBehaviors.merger.ts
var EditingBehaviorsMerger = class {
  constructor({
    mainEditingBehaviors,
    referredEditingBehaviors
  }) {
    this.referredEditingBehaviors = {};
    this.mainEditingBehaviors = new EditingBehaviors_base_default(mainEditingBehaviors);
    if (referredEditingBehaviors) {
      this.referredEditingBehaviors = referredEditingBehaviors;
    }
  }
  merge() {
    const mergedBehaviors = {
      ...this.referredEditingBehaviors,
      ...this.mainEditingBehaviors.getBehaviors()
    };
    const { errors, valid } = EditingBehaviors_base_default.validate(mergedBehaviors);
    if (!valid) {
      throw new Error(`Failed merging editing behaviors: 
         ${JSON.stringify(errors)}`);
    }
    return mergedBehaviors;
  }
};
var EditingBehaviors_merger_default = EditingBehaviorsMerger;

// src/resolver/resolvers/Elements/Elements.merger.ts
function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}
var ElementsMerger = class {
  constructor({ mainElements, referredElements }) {
    this.mainElements = {};
    this.referredElements = {};
    this.mainElements = mainElements || {};
    this.referredElements = referredElements || {};
  }
  merge() {
    if (isEmptyObject(this.referredElements) && isEmptyObject(this.mainElements)) {
      return void 0;
    }
    const updatedElementsKeys = /* @__PURE__ */ new Set([
      ...Object.keys(this.referredElements || {}),
      ...Object.keys(this.mainElements || {})
    ]);
    const updatedElements = {};
    updatedElementsKeys.forEach((elementKey) => {
      const mergedElement = new ComponentManifest_merger_default({
        manifest: this.mainElements[elementKey],
        referredManifest: this.referredElements[elementKey]
      }).merge();
      updatedElements[elementKey] = mergedElement;
    });
    return updatedElements;
  }
};
var Elements_merger_default = ElementsMerger;

// src/resolver/resolvers/Style/Style.merger.ts
var StyleMerger = class {
  constructor({ mainStyle, referredStyle }) {
    this.referredStyle = {};
    this.mainStyle = new Style_base_default(mainStyle);
    if (referredStyle) {
      this.referredStyle = referredStyle;
    }
  }
  merge() {
    const mergedStyle = {
      ...this.referredStyle,
      ...this.mainStyle.getStyle()
    };
    const { errors, valid } = Style_base_default.validate(mergedStyle);
    if (!valid) {
      throw new Error(`Failed merging styles: 
         ${JSON.stringify(errors)}`);
    }
    return mergedStyle;
  }
};
var Style_merger_default = StyleMerger;

// src/resolver/resolvers/ComponentManifest/ComponentManifest.merger.ts
var ComponentManifestMerger = class {
  constructor({ manifest, referredManifest }) {
    this.mainComponentManifest = new ComponentManifest_base_default(manifest);
    this.referredComponentManifest = new ComponentManifest_base_default(referredManifest || {});
  }
  merge() {
    const mergeEditingBehaviors = new EditingBehaviors_merger_default({
      mainEditingBehaviors: this.mainComponentManifest.getEditingBehaviors(),
      referredEditingBehaviors: this.referredComponentManifest.getEditingBehaviors()
    }).merge();
    const mergeStyle = new Style_merger_default({
      mainStyle: this.mainComponentManifest.getStyle(),
      referredStyle: this.referredComponentManifest.getStyle()
    }).merge();
    const mergedElements = new Elements_merger_default({
      mainElements: this.mainComponentManifest.getElements(),
      referredElements: this.referredComponentManifest.getElements()
    }).merge();
    const mergedManifest = new ComponentManifest_base_default();
    mergedManifest.setManifest({
      ...this.referredComponentManifest.getManifest(),
      ...this.mainComponentManifest.getManifest()
    });
    mergedManifest.setEditingBehaviors(mergeEditingBehaviors);
    mergedManifest.setStyle(mergeStyle);
    if (mergedElements) {
      mergedManifest.setElements(mergedElements);
    }
    const resolvedManifestObject = mergedManifest.getManifest();
    const { valid, errors } = ComponentManifest_base_default.validate(resolvedManifestObject);
    if (!valid) {
      throw new Error(
        componentManifestResolver_constants_default.failedMergingError({
          errors,
          mainManifest: this.mainComponentManifest.getManifest(),
          referredManifest: this.referredComponentManifest.getManifest()
        })
      );
    }
    return resolvedManifestObject;
  }
};
var ComponentManifest_merger_default = ComponentManifestMerger;

// src/resolver/resolvers/ComponentManifest/ComponentManifest.resolver.ts
var ComponentManifestResolver = class _ComponentManifestResolver extends ComponentManifest_merger_default {
  constructor({ fetchManifest, manifest, resolveElements }) {
    super({ manifest: { ...manifest } });
    this.shouldResolveElements = false;
    this.fetchManifest = fetchManifest;
    this.shouldResolveElements = resolveElements || false;
  }
  async fetchFromReference(ref) {
    this.referredComponentManifest = new ComponentManifest_base_default(await this.fetchManifest(ref));
    const fetchedManifestReferredType = this.referredComponentManifest.getReferredType();
    if (fetchedManifestReferredType) {
      const resolvedFetchedManifest = await new _ComponentManifestResolver({
        fetchManifest: this.fetchManifest,
        manifest: this.referredComponentManifest.getManifest()
      }).resolve();
      this.referredComponentManifest.setManifest(resolvedFetchedManifest);
    }
  }
  async resolveElements() {
    const elements = this.mainComponentManifest.getElements();
    for (const [elementKey, element] of Object.entries(elements || {})) {
      const elementReferredType = element.referredType;
      if (!elementReferredType) {
        continue;
      }
      const elementManifest = new _ComponentManifestResolver({
        fetchManifest: this.fetchManifest,
        manifest: element,
        resolveElements: true
      });
      const resolvedElement = await elementManifest.resolve();
      this.mainComponentManifest.setElement(elementKey, resolvedElement);
    }
  }
  async resolve() {
    const manifestReferredType = this.mainComponentManifest.getReferredType();
    if (manifestReferredType) {
      await this.fetchFromReference(manifestReferredType);
      this.mainComponentManifest.setReferredType(void 0);
    }
    const mergedManifest = super.merge();
    if (this.shouldResolveElements) {
      this.mainComponentManifest.setManifest(mergedManifest);
      await this.resolveElements();
    }
    return mergedManifest;
  }
};
var ComponentManifest_resolver_default = ComponentManifestResolver;

// src/resolver/resolvers/ComponentManifest/ComponentManifest.synced.resolver.ts
var ComponentManifestResolver2 = class _ComponentManifestResolver extends ComponentManifest_merger_default {
  constructor({ fetchManifest, manifest }) {
    super({ manifest });
    this.fetchManifest = fetchManifest;
  }
  fetchFromReference(ref) {
    this.referredComponentManifest = new ComponentManifest_base_default(this.fetchManifest(ref));
    const fetchedManifestReferredType = this.referredComponentManifest.getReferredType();
    if (fetchedManifestReferredType) {
      const resolvedFetchedManifest = new _ComponentManifestResolver({
        fetchManifest: this.fetchManifest,
        manifest: this.referredComponentManifest.getManifest()
      }).resolve();
      this.referredComponentManifest.setManifest(resolvedFetchedManifest);
    }
  }
  resolve() {
    const manifestReferredType = this.mainComponentManifest.getReferredType();
    if (manifestReferredType) {
      this.fetchFromReference(manifestReferredType);
      this.mainComponentManifest.setReferredType(void 0);
    }
    return super.merge();
  }
};
var ComponentManifest_synced_resolver_default = ComponentManifestResolver2;

// src/resolver/componentManifestResolver.ts
function createComponentManifestResolver(fetchManifest) {
  if (!fetchManifest) {
    throw new Error(componentManifestResolver_constants_default.notFetcherError);
  }
  const resolveManifest = (manifest, options) => {
    const componentManifestResolver = new ComponentManifest_resolver_default({
      fetchManifest,
      manifest,
      resolveElements: options?.resolveElements
    });
    const resolvedManifest = componentManifestResolver.resolve();
    return resolvedManifest;
  };
  return resolveManifest;
}
function createComponentManifestSyncedResolver(fetchManifest) {
  if (!fetchManifest) {
    throw new Error(componentManifestResolver_constants_default.notFetcherError);
  }
  const syncedResolveManifest = (manifest) => {
    const componentManifestResolver = new ComponentManifest_synced_resolver_default({
      fetchManifest,
      manifest
    });
    const resolvedManifest = componentManifestResolver.resolve();
    return resolvedManifest;
  };
  return syncedResolveManifest;
}
export {
  createComponentManifestResolver,
  createComponentManifestSyncedResolver
};
//# sourceMappingURL=index.js.map
